import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { supabase } from "../lib/supabase";

function useIsOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const dn = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", dn);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", dn); };
  }, []);
  return online;
}

const MAX_ATTEMPTS    = 5;
const LOCKOUT_MINUTES = 15;

function getGreeting() {
  const h   = new Date().getHours();
  const day = new Date().toLocaleDateString("en-KE", { weekday: "long" });
  if (h < 12) return { line1: "Good morning,",    line2: `${day}'s revenue is waiting.`  };
  if (h < 17) return { line1: "Good afternoon,",  line2: `How is ${day} performing?`     };
  return       { line1: "Good evening,",           line2: `${day}'s summary is ready.`   };
}

const FEATURES = [
  { title: "Revenue",   desc: "Live sales tracking"    },
  { title: "Your Team", desc: "Agent & shop oversight" },
  { title: "Inventory", desc: "Stock across locations" },
  { title: "Reports",   desc: "Daily inbox summary"    },
];

const TICKER_ITEMS = [
  { icon: "📈", text: "Track every sale your agents make — live, as it happens" },
  { icon: "🏪", text: "Monitor all your shops from one dashboard, anywhere" },
  { icon: "📦", text: "Get low-stock alerts before you run out of product" },
  { icon: "📧", text: "Receive a daily business summary in your inbox every night" },
  { icon: "👥", text: "See which agents are selling and who needs a push" },
  { icon: "💳", text: "Set agent commissions and pay fairly, every time" },
  { icon: "📷", text: "Let agents scan QR codes or SKUs to sell instantly" },
  { icon: "📊", text: "Export your reports to PDF or Excel for any meeting" },
  { icon: "💬", text: "Send messages directly to your customers from the app" },
  { icon: "🔒", text: "Your data is private — only you see your business numbers" },
];

function FeatureIcon({ index, color }: { index: number; color: string }) {
  const s = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none" as const, stroke: color, strokeWidth: "1.75", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (index === 0) return <svg {...s}><path d="M3 17L8 12L12 15L17 9L21 11"/><path d="M17 9H21V13"/></svg>;
  if (index === 1) return <svg {...s}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
  if (index === 2) return <svg {...s}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
  return <svg {...s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
}

type LockedEntry = { email: string; attempts: number; until: number };

export default function Login() {
  const { signIn } = useAuth();
  const navigate   = useNavigate();
  const isOnline   = useIsOnline();
  const { isDark, toggleTheme } = useTheme();
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [shake,        setShake]        = useState(false);
  const [lockedToast,  setLockedToast]  = useState(false);
  const [tickerIdx,    setTickerIdx]    = useState(0);
  const [tickerVisible, setTickerVisible] = useState(true);

  // ── PWA install prompt ────────────────────────────────────────────────────
  type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled,   setIsInstalled]   = useState(() =>
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
  const [showIosHint,   setShowIosHint]   = useState(false);
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const handler = (e: Event) => { e.preventDefault(); setInstallPrompt(e as BeforeInstallPromptEvent); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => { setIsInstalled(true); setInstallPrompt(null); });
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (isIos) { setShowIosHint(h => !h); return; }
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") { setIsInstalled(true); setInstallPrompt(null); }
  };

  const showInstallBtn = !isInstalled && (installPrompt !== null || isIos);

  const [lockedState, setLockedState] = useState<LockedEntry | null>(() => {
    try {
      const s = localStorage.getItem("login_locked_state");
      if (!s) return null;
      const p = JSON.parse(s) as LockedEntry;
      if (p.until && Date.now() >= p.until) { localStorage.removeItem("login_locked_state"); return null; }
      return p;
    } catch { return null; }
  });

  const normalizedEmail   = email.trim().toLowerCase();
  const emailAttempts     = lockedState?.email === normalizedEmail ? lockedState.attempts : 0;
  const emailLockedUntil  = lockedState?.email === normalizedEmail ? lockedState.until : 0;
  const isEmailLocked     = emailLockedUntil > 0 && Date.now() < emailLockedUntil;
  const remainingAttempts = MAX_ATTEMPTS - emailAttempts;
  const lockoutMinsLeft   = isEmailLocked ? Math.ceil((emailLockedUntil - Date.now()) / 60000) : 0;
  const triggerShake      = () => { setShake(true); setTimeout(() => setShake(false), 500); };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.from("profiles").select("id").eq("id", session.user.id)
          .in("role", ["supervisor", "admin"]).maybeSingle()
          .then(({ data }) => { if (!data) supabase.auth.signOut(); });
      }
    });
  }, []);

  // Ticker: fade out → advance → fade in every 3.5 s
  useEffect(() => {
    const id = setInterval(() => {
      setTickerVisible(false);
      setTimeout(() => {
        setTickerIdx(i => (i + 1) % TICKER_ITEMS.length);
        setTickerVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError("Please enter your email and password."); triggerShake(); return; }

    // Per-email lockout check — inputs stay open, only this email is blocked
    if (lockedState?.email === normalizedEmail && lockedState.until > Date.now()) {
      const minsLeft = Math.ceil((lockedState.until - Date.now()) / 60000);
      setError(`Too many failed attempts for this email. Try again in ${minsLeft} minute${minsLeft !== 1 ? "s" : ""}.`);
      triggerShake(); return;
    }

    setError(""); setLoading(true);
    const { error: authError } = await signIn(email.trim(), password);
    if (authError) {
      if (authError === "__locked__") {
        setLockedToast(true);
        setLoading(false); return;
      }
      if (authError === "offline_no_cache") {
        setError("No offline data found for this account. You must sign in online at least once on this device.");
        setLoading(false); triggerShake(); return;
      }
      const prevAttempts = lockedState?.email === normalizedEmail ? lockedState.attempts : 0;
      const n = prevAttempts + 1;
      let newEntry: LockedEntry;
      if (n >= MAX_ATTEMPTS) {
        const until = Date.now() + LOCKOUT_MINUTES * 60 * 1000;
        newEntry = { email: normalizedEmail, attempts: n, until };
        setError(`Too many failed attempts. This email is locked for ${LOCKOUT_MINUTES} minutes.`);
      } else {
        newEntry = { email: normalizedEmail, attempts: n, until: 0 };
        const left = MAX_ATTEMPTS - n;
        setError(`${authError} ${left} attempt${left !== 1 ? "s" : ""} remaining.`);
      }
      setLockedState(newEntry);
      localStorage.setItem("login_locked_state", JSON.stringify(newEntry));
      setLoading(false); triggerShake(); return;
    }
    setLockedState(null);
    localStorage.removeItem("login_locked_state");
    // Offline mode — go straight to dashboard (profile already set in context)
    if (!isOnline) { setLoading(false); navigate("/dashboard"); return; }
    setLoading(false); navigate("/dashboard");
  };

  const { line1, line2 } = getGreeting();
  const D = isDark;

  const pageBg    = D ? "linear-gradient(150deg,#07101f 0%,#0a1628 55%,#07101f 100%)"
                      : "linear-gradient(150deg,#f0f4ff 0%,#e8f0fe 40%,#fef9f0 100%)";
  const cardBg    = D ? "#111c2e" : "#ffffff";
  const cardShadow= D ? "0 24px 64px rgba(0,0,0,0.6),0 4px 18px rgba(0,0,0,0.35)"
                      : "0 24px 64px rgba(15,23,42,0.13),0 4px 18px rgba(15,23,42,0.07)";
  const formBg    = D ? "#111c2e" : "#ffffff";
  const textMain  = D ? "#f1f0ed" : "#0f172a";
  const textSub   = D ? "rgba(241,240,237,0.55)" : "#334155";
  const textMuted = D ? "rgba(241,240,237,0.32)" : "#64748b";
  const divider   = D ? "rgba(255,255,255,0.07)"  : "#e2e8f0";
  const labelClr  = D ? "rgba(241,240,237,0.65)"  : "#1e3a5f";
  const inputBg   = D ? "rgba(255,255,255,0.06)"  : "#f8fafc";
  const inputBdr  = D ? "rgba(255,255,255,0.11)"  : "#cbd5e1";
  const phClr     = D ? "rgba(255,255,255,0.25)"  : "#94a3b8";
  const gridBg    = D ? "rgba(255,255,255,0.05)"  : "#f8fafc";
  const gridBdr   = D ? "rgba(255,255,255,0.09)"  : "#e2e8f0";
  const gridIcon  = D ? "rgba(251,191,36,0.12)"   : "#fefce8";
  const gridIconB = D ? "rgba(251,191,36,0.2)"    : "#fde68a";
  const quoteBg   = D ? "rgba(255,255,255,0.04)"  : "#f0f4ff";
  const quoteBdr  = D ? "rgba(99,102,241,0.3)"    : "#c7d2fe";
  const tglBg     = D ? "rgba(255,255,255,0.07)"  : "rgba(15,23,42,0.06)";
  const tglBdr    = D ? "rgba(255,255,255,0.13)"  : "rgba(15,23,42,0.1)";
  const tglClr    = D ? "#f1f0ed" : "#0f172a";
  const footerBdr = D ? "rgba(255,255,255,0.07)"  : "#e2e8f0";
  const errBg     = D ? "rgba(220,38,38,0.09)"    : "#fef2f2";
  const errBdr    = D ? "rgba(220,38,38,0.28)"    : "rgba(220,38,38,0.22)";
  const errTitle  = D ? "#f87171" : "#dc2626";
  const errHint   = D ? "rgba(241,240,237,0.48)"  : "#9a3412";
  const captionClr= D ? "rgba(241,240,237,0.22)"  : "#94a3b8";
  const dotOff    = D ? "rgba(255,255,255,0.13)"  : "#cbd5e1";

  return (
    <div style={{ height:"100dvh", overflow:"hidden", background:pageBg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"12px 16px", fontFamily:"'Inter',sans-serif", transition:"background 0.35s" }}>

      {/* ── Locked toast — top right ─────────────────────────────────────── */}
      {lockedToast && (
        <div style={{
          position: "fixed", top: 16, right: 16, zIndex: 9999,
          width: "min(420px, calc(100vw - 32px))",
          background: D ? "#1a0a0a" : "#fff1f1",
          border: "1px solid rgba(239,68,68,0.45)",
          borderRadius: 14,
          padding: "16px 18px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.35), 0 2px 8px rgba(239,68,68,0.2)",
          animation: "toastIn 0.3s cubic-bezier(0.22,1,0.36,1) both",
          display: "flex", alignItems: "flex-start", gap: 14,
        }}>
          {/* Icon */}
          <div style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 10, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
            🔒
          </div>
          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 14, color: D ? "#fca5a5" : "#dc2626", marginBottom: 4 }}>
              Account Locked
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: D ? "rgba(252,165,165,0.75)" : "#7f1d1d", lineHeight: 1.55 }}>
              Your account has been locked by the administrator. Please contact support to regain access.
            </div>
          </div>
          {/* Close */}
          <button
            onClick={() => setLockedToast(false)}
            style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: D ? "rgba(252,165,165,0.5)" : "#ef4444", fontSize: 18, lineHeight: 1, padding: "2px 4px", marginTop: -2 }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,500;0,600;0,700;0,800;1,400&family=Inter:wght@400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html, body { overflow: hidden; height: 100%; }
        @keyframes riseUp   { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin     { to{transform:rotate(360deg)} }
        @keyframes shake    { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-6px)} 40%,80%{transform:translateX(6px)} }
        @keyframes errIn    { from{opacity:0;transform:translateY(-3px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:0.35} }
        @keyframes toastIn  { from{opacity:0;transform:translateY(-16px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes toastOut { from{opacity:1;transform:translateY(0) scale(1)} to{opacity:0;transform:translateY(-12px) scale(0.97)} }
        @keyframes installGlow { 0%,100%{box-shadow:0 4px 18px rgba(29,78,216,0.28)} 50%{box-shadow:0 4px 26px rgba(59,130,246,0.55)} }
        .install-btn { animation: installGlow 2.8s ease-in-out infinite; }
        .install-btn:hover:not(:disabled){ opacity:0.88!important; transform:scale(1.015); }
        .install-btn:active:not(:disabled){ transform:scale(0.97); }
        .card-l  { animation: riseUp 0.45s cubic-bezier(0.22,1,0.36,1) 0.05s both; }
        .card-r  { animation: riseUp 0.45s cubic-bezier(0.22,1,0.36,1) 0.15s both; }
        .shaking { animation: shake 0.42s ease !important; }
        .err-in  { animation: errIn 0.2s ease; }
        .tgl     { transition:all 0.2s; } .tgl:hover { opacity:0.8; }
        .own-input {
          width:100%; background:${inputBg};
          border:1.5px solid ${inputBdr}; border-radius:10px;
          padding:10px 16px 10px 42px; color:${textMain};
          font-size:14px; font-family:'Inter',sans-serif;
          transition:border-color 0.18s,box-shadow 0.18s;
          -webkit-appearance:none; outline:none;
        }
        .own-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.14); }
        .own-input::placeholder { color:${phClr}; }
        .own-input:disabled { opacity:0.5; cursor:not-allowed; }
        .own-btn {
          width:100%; border:none; border-radius:11px; padding:12px;
          background:linear-gradient(135deg,#1d4ed8 0%,#1e3a5f 100%);
          color:#fff; font-family:'Plus Jakarta Sans',sans-serif; font-weight:700;
          font-size:14.5px; letter-spacing:0.02em; cursor:pointer;
          box-shadow:0 4px 16px rgba(29,78,216,0.32);
          transition:opacity 0.18s,transform 0.12s,box-shadow 0.18s;
          display:flex; align-items:center; justify-content:center; gap:9px;
          -webkit-tap-highlight-color:transparent;
        }
        .own-btn:hover:not(:disabled) { opacity:0.88; box-shadow:0 6px 22px rgba(29,78,216,0.42); }
        .own-btn:active:not(:disabled){ transform:scale(0.98); }
        .own-btn:disabled { opacity:0.45; cursor:not-allowed; box-shadow:none; }
        .feat-card {
          flex:1 1 calc(50% - 5px); background:${gridBg};
          border:1px solid ${gridBdr}; border-radius:10px;
          padding:9px 10px; display:flex; flex-direction:column; gap:5px;
          transition:border-color 0.2s;
        }
        .feat-card:hover { border-color:rgba(59,130,246,0.35); }
        @media (max-width: 640px) {
          .card-l { display:none !important; }
          .card-r { flex:1 1 100% !important; max-width:100% !important; }
        }
      `}</style>

      {/* ── Theme toggle ── */}
      <button className="tgl" onClick={toggleTheme} style={{ position:"fixed", top:14, right:14, zIndex:200, background:tglBg, border:`1px solid ${tglBdr}`, borderRadius:40, padding:"6px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:tglClr, fontSize:12, fontFamily:"'Inter',sans-serif", fontWeight:600, backdropFilter:"blur(8px)" }}>
        {D ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>Light</>
           : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>Dark</>}
      </button>

      <div style={{ display:"flex", flexWrap:"wrap", gap:16, width:"100%", maxWidth:880, alignItems:"stretch", justifyContent:"center" }}>

        {/* ══ LEFT — Command overview card ══ */}
        <div className="card-l" style={{ flex:"1 1 300px", maxWidth:400, borderRadius:20, background:cardBg, boxShadow:cardShadow, padding:"20px 22px", display:"flex", flexDirection:"column", transition:"background 0.35s" }}>

          {/* Brand */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
            <img src="/Qash.png" alt="Qash" style={{ height:40, objectFit:"contain" }} />
          </div>

          {/* Headline */}
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:"clamp(20px,2.8vw,27px)", color:textMain, lineHeight:1.12, letterSpacing:"-0.03em", marginBottom:6 }}>
            Your business,<br/><span style={{ background:"linear-gradient(90deg,#1d4ed8,#3b82f6)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>fully in command.</span>
          </div>
          <div style={{ fontSize:13, color:textSub, lineHeight:1.65, marginBottom:12, fontWeight:400 }}>
            Complete visibility over every sale, every agent, and every product — in real time.
          </div>

          {/* 2×2 feature grid */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12 }}>
            {FEATURES.map((f, i) => (
              <div key={f.title} className="feat-card">
                <div style={{ width:28, height:28, borderRadius:8, background:gridIcon, border:`1px solid ${gridIconB}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <FeatureIcon index={i} color={D ? "#93c5fd" : "#1d4ed8"} />
                </div>
                <div>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:12, color:textMain, marginBottom:1, letterSpacing:"-0.01em" }}>{f.title}</div>
                  <div style={{ fontSize:11, color:textMuted, lineHeight:1.35 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ borderTop:`1px solid ${divider}`, margin:"0 0 10px" }} />

          {/* Animated feature ticker */}
          <div style={{
            background: quoteBg, border: `1px solid ${quoteBdr}`,
            borderLeft: `3px solid #3b82f6`, borderRadius: "0 8px 8px 0",
            padding: "10px 13px", marginBottom: 10, minHeight: 44,
            display: "flex", alignItems: "center", gap: 10,
            overflow: "hidden",
          }}>
            <span style={{
              fontSize: 16, flexShrink: 0,
              opacity: tickerVisible ? 1 : 0,
              transition: "opacity 0.35s ease",
            }}>
              {TICKER_ITEMS[tickerIdx].icon}
            </span>
            <div style={{
              fontSize: 12, color: textSub, lineHeight: 1.55,
              fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 500,
              opacity: tickerVisible ? 1 : 0,
              transform: tickerVisible ? "translateY(0)" : "translateY(6px)",
              transition: "opacity 0.35s ease, transform 0.35s ease",
            }}>
              {TICKER_ITEMS[tickerIdx].text}
            </div>
          </div>

          <div style={{ flex:1 }} />

          {/* ── PWA install button (left card) ── */}
          {showInstallBtn && (
            <div style={{ marginBottom:10 }}>
              <button
                className="install-btn"
                onClick={handleInstall}
                style={{
                  width:"100%", border:"none", borderRadius:11, padding:"11px 14px",
                  background:"linear-gradient(135deg,#1d4ed8 0%,#3b82f6 100%)",
                  color:"#fff", cursor:"pointer", display:"flex", alignItems:"center",
                  justifyContent:"center", gap:9, transition:"opacity 0.18s,transform 0.12s",
                  fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:13.5,
                  letterSpacing:"0.01em",
                }}
              >
                {/* Download icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {isIos ? "Add to Home Screen" : "Install QASHUP App"}
              </button>
              {/* iOS share-sheet hint */}
              {isIos && showIosHint && (
                <div style={{
                  marginTop:8, padding:"10px 13px",
                  background: D ? "rgba(59,130,246,0.1)" : "#eff6ff",
                  border:"1px solid rgba(59,130,246,0.3)", borderRadius:10,
                  fontSize:11.5, color: D ? "#93c5fd" : "#1d4ed8",
                  lineHeight:1.6, fontFamily:"'Inter',sans-serif",
                }}>
                  Tap the <strong>Share</strong> button{" "}
                  <svg style={{ verticalAlign:"middle" }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                  {" "}in Safari, then choose <strong>"Add to Home Screen"</strong>.
                </div>
              )}
            </div>
          )}

          {/* Status */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background: isOnline ? "#22c55e" : "#ef4444", display:"inline-block", animation:"pulse 2s ease-in-out infinite" }} />
            <span style={{ fontSize:10.5, color: isOnline ? textMuted : "#f87171", fontFamily:"'Inter',sans-serif", letterSpacing:"0.04em" }}>
              {isOnline ? "All systems operational · Kenya" : "Offline — cached login available"}
            </span>
          </div>
        </div>

        {/* ══ RIGHT — Form card ══ */}
        <div className={`card-r${shake ? " shaking" : ""}`} style={{ flex:"1 1 300px", maxWidth:420, borderRadius:20, overflow:"hidden", boxShadow:cardShadow }}>

          {/* Navy gradient header with arch bottom */}
          <div style={{ background:"linear-gradient(145deg,#0f172a 0%,#1e3a5f 55%,#1d4ed8 100%)", padding:"18px 24px 34px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-60, right:-60, width:180, height:180, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.07)", pointerEvents:"none" }} />
            <div style={{ position:"absolute", top:-22, right:-22, width:100, height:100, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.05)", pointerEvents:"none" }} />
            <div style={{ position:"absolute", top:18, left:24, width:5, height:5, borderRadius:"50%", background:"rgba(251,191,36,0.7)" }} />
            <div style={{ position:"absolute", top:18, left:35, width:4, height:4, borderRadius:"50%", background:"rgba(251,191,36,0.4)" }} />
            <div style={{ position:"relative" }}>
              <div style={{ fontSize:10, color:"rgba(251,191,36,0.85)", fontFamily:"'Inter',sans-serif", fontWeight:600, letterSpacing:"0.1em", marginBottom:7 }}>OWNER PORTAL</div>
              <div style={{ fontSize:"clamp(17px,3vw,21px)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, color:"#fff", letterSpacing:"-0.03em", lineHeight:1.18, marginBottom:5 }}>{line1}</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)", lineHeight:1.55, fontFamily:"'Inter',sans-serif", fontWeight:400 }}>{line2} Sign in to your dashboard.</div>
            </div>
            <div style={{ position:"absolute", bottom:-1, left:0, right:0, height:26, background:formBg, borderRadius:"50% 50% 0 0 / 100% 100% 0 0", transition:"background 0.35s" }} />
          </div>

          {/* Form body */}
          <div style={{ background:formBg, padding:"4px 22px 18px", transition:"background 0.35s" }}>

            <form onSubmit={handleLogin} style={{ display:"flex", flexDirection:"column", gap:10, marginTop:6 }}>

                {!isOnline && (
                  <div style={{ display:"flex", alignItems:"flex-start", gap:9, padding:"10px 13px", background: D ? "rgba(220,38,38,0.08)" : "#fff5f5", border:`1px solid ${D ? "rgba(220,38,38,0.25)" : "rgba(220,38,38,0.2)"}`, borderRadius:9 }}>
                    <span style={{ fontSize:15, flexShrink:0, marginTop:1 }}>📵</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:"#ef4444", marginBottom:2, fontFamily:"'Inter',sans-serif" }}>You're offline</div>
                      <div style={{ fontSize:10.5, color:errHint, fontFamily:"'Inter',sans-serif", lineHeight:1.5 }}>Sign in with your cached credentials to continue working.</div>
                    </div>
                  </div>
                )}

                {isEmailLocked && (
                  <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 13px", background: D ? "rgba(220,38,38,0.09)" : "#fef2f2", border:`1px solid ${errBdr}`, borderRadius:9 }}>
                    <span style={{ flexShrink:0, display:"flex", color:errTitle }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:errTitle, fontFamily:"'Inter',sans-serif" }}>This email is locked</div>
                      <div style={{ fontSize:10.5, color:errHint, fontFamily:"'Inter',sans-serif", marginTop:1 }}>
                        Try again in <strong>{lockoutMinsLeft} min{lockoutMinsLeft !== 1 ? "s" : ""}</strong> · You can sign in with a different email
                      </div>
                    </div>
                  </div>
                )}

                {emailAttempts > 0 && !isEmailLocked && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background: D ? "rgba(251,191,36,0.08)" : "rgba(251,191,36,0.07)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:9, gap:8 }}>
                    <span style={{ fontSize:11.5, color:"#d97706", fontWeight:600 }}>{remainingAttempts} attempt{remainingAttempts !== 1 ? "s" : ""} left</span>
                    <div style={{ display:"flex", gap:4 }}>
                      {Array.from({ length:MAX_ATTEMPTS }).map((_,i) => (
                        <div key={i} style={{ width:6, height:6, borderRadius:"50%", background: i < emailAttempts ? "#ef4444" : dotOff }} />
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:labelClr, paddingLeft:2, letterSpacing:"0.01em" }}>Email address</label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.5, display:"flex" }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={D ? "#94a3b8" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </span>
                    <input className="own-input" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} placeholder="owner@yourbusiness.com" autoComplete="email" disabled={loading} />
                  </div>
                </div>

                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <label style={{ fontSize:12, fontWeight:600, color:labelClr, paddingLeft:2, letterSpacing:"0.01em" }}>Password</label>
                    <Link to="/forgot-password" style={{ fontSize:11.5, color:"#3b82f6", textDecoration:"none" }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration="underline"}
                      onMouseLeave={e => e.currentTarget.style.textDecoration="none"}>
                      Forgot?
                    </Link>
                  </div>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.5, display:"flex" }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={D ? "#94a3b8" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </span>
                    <input className={`own-input${error ? " err" : ""}`} type={showPassword ? "text" : "password"} value={password} onChange={e => { setPassword(e.target.value); setError(""); }} placeholder="••••••••" autoComplete="current-password" disabled={loading} style={{ paddingRight:44 }} />
                    <button type="button" onClick={() => setShowPassword(p => !p)} tabIndex={-1} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color: D ? "rgba(255,255,255,0.35)" : "#94a3b8", padding:4, display:"flex", alignItems:"center" }}>
                      {showPassword
                        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="err-in" style={{ background:errBg, border:`1px solid ${errBdr}`, borderRadius:9, padding:"9px 13px", display:"flex", gap:9, alignItems:"flex-start" }}>
                    <span style={{ flexShrink:0, marginTop:1, display:"flex", color:errTitle }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
                    <div>
                      <div style={{ color:errTitle, fontSize:12.5, fontWeight:600, marginBottom:1, fontFamily:"'Inter',sans-serif" }}>{error}</div>
                      <div style={{ color:errHint, fontSize:10.5, fontFamily:"'Inter',sans-serif", lineHeight:1.5 }}>Check your credentials and try again.</div>
                    </div>
                  </div>
                )}

                <button type="submit" disabled={loading} className="own-btn" style={{ marginTop:2 }}>
                  {loading
                    ? <><span style={{ width:15, height:15, border:"2.5px solid rgba(255,255,255,0.3)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin 0.7s linear infinite" }} />Signing in…</>
                    : "Sign in to dashboard →"
                  }
                </button>
              </form>

            <div style={{ marginTop:12, paddingTop:10, borderTop:`1px solid ${footerBdr}`, display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
              <div style={{ fontSize:12.5, color:textMuted, textAlign:"center" }}>
                New to QASHUP?{" "}
                <Link to="/register" style={{ color:"#3b82f6", fontWeight:600, textDecoration:"none" }}
                  onMouseEnter={e => e.currentTarget.style.textDecoration="underline"}
                  onMouseLeave={e => e.currentTarget.style.textDecoration="none"}>
                  Create a free account
                </Link>
              </div>

              {/* ── PWA install nudge (right card, shown on mobile when left card is hidden) ── */}
              {showInstallBtn && (
                <div style={{ width:"100%" }}>
                  <button
                    className="install-btn"
                    onClick={handleInstall}
                    style={{
                      width:"100%", border:"none", borderRadius:9, padding:"9px 14px",
                      background:"linear-gradient(135deg,#1d4ed8 0%,#3b82f6 100%)",
                      color:"#fff", cursor:"pointer", display:"flex", alignItems:"center",
                      justifyContent:"center", gap:8, transition:"opacity 0.18s,transform 0.12s",
                      fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:12.5,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    {isIos ? "Add to Home Screen" : "Install QASHUP App"}
                  </button>
                  {isIos && showIosHint && (
                    <div style={{
                      marginTop:7, padding:"9px 12px",
                      background: D ? "rgba(59,130,246,0.1)" : "#eff6ff",
                      border:"1px solid rgba(59,130,246,0.3)", borderRadius:9,
                      fontSize:11, color: D ? "#93c5fd" : "#1d4ed8",
                      lineHeight:1.6, fontFamily:"'Inter',sans-serif", textAlign:"center",
                    }}>
                      Tap <strong>Share</strong>{" "}
                      <svg style={{ verticalAlign:"middle" }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                      {" "}in Safari → <strong>Add to Home Screen</strong>
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize:10, color:captionClr, fontFamily:"'Inter',sans-serif", letterSpacing:"0.05em" }}>
                QASHUP v1.0 · Owner &amp; supervisor access only
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
